import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  FaArrowLeft,
  FaSearch,
  FaSpinner,
  FaEye,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import "./admPatrocinadoresList.css";
import "./admAgendaLogs.css";

const ACOES = [
  { value: "", label: "Todas as ações" },
  { value: "criar", label: "Criar" },
  { value: "alterar", label: "Alterar" },
  { value: "excluir", label: "Excluir" },
  { value: "substituir", label: "Substituir" },
  {
    value: "substituir_candidato_incompativel",
    label: "Substituir — candidato incompatível",
  },
  { value: "remarcar", label: "Remarcar" },
  { value: "editar_horario", label: "Editar horário" },
  { value: "marcar", label: "Marcar" },
  { value: "adicionar_participante", label: "Adicionar participante" },
  { value: "remover_participante", label: "Remover participante" },
  { value: "remover_participantes", label: "Remover participantes" },
  { value: "checkin", label: "Check-in" },
  { value: "reverter_checkin", label: "Reverter check-in" },
];

const LABEL_ACAO = Object.fromEntries(
  ACOES.filter((a) => a.value).map((a) => [a.value, a.label]),
);

function formatarDataHora(valor) {
  if (!valor) return "—";
  try {
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return String(valor);
    return d.toLocaleString("pt-BR");
  } catch {
    return String(valor);
  }
}

function nomeUsuario(u) {
  if (!u) return "—";
  return u.nome || `#${u.id}`;
}

const AdmAgendaLogs = () => {
  const navigate = useNavigate();
  const { getUsuariosLista, getPatrocinadoresLista } = useApp();
  const { usuario } = useAuth();

  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 25,
    current_page: 1,
    last_page: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [acao, setAcao] = useState("");
  const [idUsuarioAtor, setIdUsuarioAtor] = useState("");
  const [idConvidado, setIdConvidado] = useState("");
  const [idInfoPatrocinador, setIdInfoPatrocinador] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [page, setPage] = useState(1);

  const [usuarios, setUsuarios] = useState([]);
  const [patrocinadores, setPatrocinadores] = useState([]);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca), 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    if (usuario && usuario.tipo !== "administracao") {
      navigate("/private/adm");
    }
  }, [usuario, navigate]);

  useEffect(() => {
    if (usuario?.tipo !== "administracao") return undefined;
    let ativo = true;
    (async () => {
      try {
        const [listaUsuarios, listaPatro] = await Promise.all([
          getUsuariosLista(),
          getPatrocinadoresLista(),
        ]);
        if (!ativo) return;
        setUsuarios(Array.isArray(listaUsuarios) ? listaUsuarios : []);
        setPatrocinadores(Array.isArray(listaPatro) ? listaPatro : []);
      } catch (err) {
        console.error("Erro ao carregar filtros:", err);
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carrega filtros uma vez ao entrar
  }, [usuario?.tipo]);

  const atores = useMemo(
    () =>
      usuarios.filter((u) =>
        ["administracao", "administracao_dash", "recepcao"].includes(u.tipo),
      ),
    [usuarios],
  );

  const convidados = useMemo(
    () => usuarios.filter((u) => u.tipo === "convidado"),
    [usuarios],
  );

  const carregarLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const body = {
        page,
        per_page: 25,
      };
      if (buscaDebounced.trim()) body.busca = buscaDebounced.trim();
      if (acao) body.acao = acao;
      if (idUsuarioAtor) body.id_usuario_ator = Number(idUsuarioAtor);
      if (idConvidado) body.id_convidado = Number(idConvidado);
      if (idInfoPatrocinador) {
        body.id_info_patrocinador = Number(idInfoPatrocinador);
      }
      if (dataInicio) body.data_inicio = dataInicio;
      if (dataFim) body.data_fim = dataFim;

      const resp = await api.post("/agenda-evento-logs", body);
      setLogs(Array.isArray(resp.data?.dados) ? resp.data.dados : []);
      setMeta(
        resp.data?.meta || {
          total: 0,
          per_page: 25,
          current_page: page,
          last_page: 1,
        },
      );
    } catch (err) {
      console.error("Erro ao carregar logs:", err);
      setError(
        err?.response?.data?.mensagem || "Erro ao carregar logs de agenda",
      );
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    buscaDebounced,
    acao,
    idUsuarioAtor,
    idConvidado,
    idInfoPatrocinador,
    dataInicio,
    dataFim,
  ]);

  useEffect(() => {
    if (usuario?.tipo === "administracao") {
      carregarLogs();
    }
  }, [usuario, carregarLogs]);

  const limparFiltros = () => {
    setBusca("");
    setBuscaDebounced("");
    setAcao("");
    setIdUsuarioAtor("");
    setIdConvidado("");
    setIdInfoPatrocinador("");
    setDataInicio("");
    setDataFim("");
    setPage(1);
  };

  if (usuario?.tipo !== "administracao") {
    return null;
  }

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho dos logs">
          <div className="apl-header-text">
            <h1>Logs de agenda</h1>
            <p>
              Histórico de criações, alterações, exclusões e substituições de
              agendas e reuniões.
            </p>
          </div>

          <div className="apl-toolbar">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="apl-toolbar aal-filters">
          <div className="apl-search apl-search--wide">
            <span className="apl-search-icon" aria-hidden>
              <FaSearch size={14} />
            </span>
            <input
              type="search"
              className="apl-search-input"
              placeholder="Buscar no resumo…"
              value={busca}
              onChange={(e) => {
                setPage(1);
                setBusca(e.target.value);
              }}
              disabled={isLoading}
              aria-label="Buscar logs"
            />
          </div>

          <select
            className="aal-select"
            value={acao}
            onChange={(e) => {
              setPage(1);
              setAcao(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Filtrar por ação"
          >
            {ACOES.map((op) => (
              <option key={op.value || "todas"} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>

          <select
            className="aal-select"
            value={idUsuarioAtor}
            onChange={(e) => {
              setPage(1);
              setIdUsuarioAtor(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Filtrar por usuário"
          >
            <option value="">Todos os usuários (ator)</option>
            {atores.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>

          <select
            className="aal-select"
            value={idConvidado}
            onChange={(e) => {
              setPage(1);
              setIdConvidado(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Filtrar por convidado"
          >
            <option value="">Todos os convidados</option>
            {convidados.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>

          <select
            className="aal-select"
            value={idInfoPatrocinador}
            onChange={(e) => {
              setPage(1);
              setIdInfoPatrocinador(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Filtrar por patrocinador"
          >
            <option value="">Todos os patrocinadores</option>
            {patrocinadores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.patrocinador || p.usuario?.nome || p.nome || `#${p.id}`}
              </option>
            ))}
          </select>

          <input
            type="date"
            className="aal-select"
            value={dataInicio}
            onChange={(e) => {
              setPage(1);
              setDataInicio(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Data início"
          />
          <input
            type="date"
            className="aal-select"
            value={dataFim}
            onChange={(e) => {
              setPage(1);
              setDataFim(e.target.value);
            }}
            disabled={isLoading}
            aria-label="Data fim"
          />

          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={limparFiltros}
            disabled={isLoading}
          >
            Limpar
          </button>
        </div>

        {error ? (
          <div className="apl-empty" role="alert">
            <p>{error}</p>
          </div>
        ) : null}

        {isLoading ? (
          <div className="apl-empty">
            <FaSpinner className="aal-spin" aria-hidden />
            <p>Carregando logs…</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="apl-empty">
            <p>Nenhum log encontrado com os filtros atuais.</p>
          </div>
        ) : (
          <div className="apl-card">
            <table className="apl-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ação</th>
                  <th>Agenda</th>
                  <th>Ator</th>
                  <th>Convidado</th>
                  <th>Patrocinador</th>
                  <th>Resumo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatarDataHora(log.created_at)}</td>
                    <td>
                      <span className="aal-badge">
                        {LABEL_ACAO[log.acao] || log.acao}
                      </span>
                    </td>
                    <td>
                      {log.id_agenda ? `#${log.id_agenda}` : "—"}
                      {log.id_agenda_relacionada
                        ? ` → #${log.id_agenda_relacionada}`
                        : ""}
                    </td>
                    <td>{nomeUsuario(log.ator)}</td>
                    <td>{nomeUsuario(log.convidado)}</td>
                    <td>{nomeUsuario(log.usuario_patrocinador)}</td>
                    <td className="aal-resumo">{log.resumo || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="apl-btn apl-btn--ghost aal-btn-icon"
                        onClick={() => setDetalhe(log)}
                        title="Ver antes/depois"
                      >
                        <FaEye size={14} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="apl-footer">
          <span>
            {meta.total} registro{meta.total === 1 ? "" : "s"}
            {meta.last_page > 1
              ? ` · página ${meta.current_page} de ${meta.last_page}`
              : ""}
          </span>
          {meta.last_page > 1 ? (
            <div className="aal-pagination">
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                disabled={isLoading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                disabled={isLoading || page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          ) : null}
        </footer>
      </div>

      {detalhe ? (
        <div
          className="aal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Detalhe do log"
          onClick={() => setDetalhe(null)}
        >
          <div
            className="aal-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aal-modal-header">
              <div>
                <h2>
                  {LABEL_ACAO[detalhe.acao] || detalhe.acao}
                  {detalhe.id_agenda ? ` · #${detalhe.id_agenda}` : ""}
                </h2>
                <p>{formatarDataHora(detalhe.created_at)}</p>
              </div>
              <button
                type="button"
                className="apl-btn apl-btn--ghost aal-btn-icon"
                onClick={() => setDetalhe(null)}
                aria-label="Fechar"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <p className="aal-modal-resumo">{detalhe.resumo || "—"}</p>
            <div className="aal-modal-grid">
              <div>
                <h3>Antes</h3>
                <pre>
                  {detalhe.dados_antes
                    ? JSON.stringify(detalhe.dados_antes, null, 2)
                    : "—"}
                </pre>
              </div>
              <div>
                <h3>Depois</h3>
                <pre>
                  {detalhe.dados_depois
                    ? JSON.stringify(detalhe.dados_depois, null, 2)
                    : "—"}
                </pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdmAgendaLogs;
